import React, { useState, useEffect } from 'react';
import { X, Upload, Trash2, Plus } from 'lucide-react';
import type { AdminVendor, VendorFormData } from '../../types/admin';

interface Props {
  vendor?: AdminVendor | null;
  onClose: () => void;
  onSubmit: (data: VendorFormData) => Promise<void>;
}

export default function VendorFormModal({ vendor, onClose, onSubmit }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<VendorFormData>({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    business_license: '',
    service_categories: [],
    pricing_structure: {
      min_price: 0,
      max_price: 0,
      unit: 'per event',
      currency: 'LKR'
    },
    availability: {
      days: [],
      hours: ''
    },
    payment_terms: '',
    contract_details: '',
    photos: []
  });

  const serviceCategories = [
    'Photography', 'Videography', 'Catering', 'Music & DJ', 'Flowers & Decor',
    'Transportation', 'Makeup & Beauty', 'Wedding Planning', 'Lighting', 'Security'
  ];

  const weekDays = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  useEffect(() => {
    if (vendor) {
      setFormData({
        name: vendor.name,
        contact_person: vendor.contact_person,
        email: vendor.email,
        phone: vendor.phone,
        address: vendor.address,
        website: vendor.website || '',
        business_license: vendor.business_license || '',
        service_categories: vendor.service_categories,
        pricing_structure: vendor.pricing_structure as any,
        availability: vendor.availability as any,
        payment_terms: vendor.payment_terms || '',
        contract_details: vendor.contract_details || '',
        photos: vendor.photos
      });
    }
  }, [vendor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      await onSubmit(formData);
    } catch (err) {
      console.error('Error submitting vendor form:', err);
      setError('Failed to save vendor. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryToggle = (category: string) => {
    setFormData(prev => ({
      ...prev,
      service_categories: prev.service_categories.includes(category)
        ? prev.service_categories.filter(c => c !== category)
        : [...prev.service_categories, category]
    }));
  };

  const handleDayToggle = (day: string) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        days: prev.availability.days.includes(day)
          ? prev.availability.days.filter(d => d !== day)
          : [...prev.availability.days, day]
      }
    }));
  };

  const addPhotoUrl = () => {
    const url = prompt('Enter photo URL:');
    if (url) {
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, url]
      }));
    }
  };

  const removePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white">
            {vendor ? 'Edit Vendor' : 'Add New Vendor'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-900/50 text-red-200 p-3 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-900/50 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-white">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Vendor Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Contact Person *
                </label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Phone *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Business Address *
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  rows={2}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  placeholder="https://"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Business License Number
                </label>
                <input
                  type="text"
                  value={formData.business_license}
                  onChange={(e) => setFormData({ ...formData, business_license: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                />
              </div>
            </div>
          </div>

          {/* Service Categories */}
          <div className="bg-gray-900/50 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-white">Service Categories *</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {serviceCategories.map(category => (
                <label key={category} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.service_categories.includes(category)}
                    onChange={() => handleCategoryToggle(category)}
                    className="h-4 w-4 bg-gray-800 border-gray-700 rounded text-primary focus:ring-primary"
                  />
                  <span className="text-gray-300 text-sm">{category}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Pricing Structure */}
          <div className="bg-gray-900/50 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-white">Pricing Structure</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Min Price
                </label>
                <input
                  type="number"
                  value={formData.pricing_structure.min_price === 0 ? '' : formData.pricing_structure.min_price}
                  onChange={(e) => setFormData({
                    ...formData,
                    pricing_structure: {
                      ...formData.pricing_structure,
                      min_price: e.target.value === '' ? 0 : Number(e.target.value)
                    }
                  })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  min="0"
                  placeholder="Enter minimum price"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Max Price
                </label>
                <input
                  type="number"
                  value={formData.pricing_structure.max_price === 0 ? '' : formData.pricing_structure.max_price}
                  onChange={(e) => setFormData({
                    ...formData,
                    pricing_structure: {
                      ...formData.pricing_structure,
                      max_price: e.target.value === '' ? 0 : Number(e.target.value)
                    }
                  })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  min="0"
                  placeholder="Enter maximum price"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Unit
                </label>
                <select
                  value={formData.pricing_structure.unit}
                  onChange={(e) => setFormData({
                    ...formData,
                    pricing_structure: {
                      ...formData.pricing_structure,
                      unit: e.target.value
                    }
                  })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                >
                  <option value="per event">Per Event</option>
                  <option value="per hour">Per Hour</option>
                  <option value="per day">Per Day</option>
                  <option value="per person">Per Person</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Currency
                </label>
                <select
                  value={formData.pricing_structure.currency}
                  onChange={(e) => setFormData({
                    ...formData,
                    pricing_structure: {
                      ...formData.pricing_structure,
                      currency: e.target.value
                    }
                  })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                >
                  <option value="LKR">LKR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>
          </div>

          {/* Availability */}
          <div className="bg-gray-900/50 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-white">Availability</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Available Days
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {weekDays.map(day => (
                    <label key={day} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.availability.days.includes(day)}
                        onChange={() => handleDayToggle(day)}
                        className="h-4 w-4 bg-gray-800 border-gray-700 rounded text-primary focus:ring-primary"
                      />
                      <span className="text-gray-300 text-sm">{day}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Working Hours
                </label>
                <input
                  type="text"
                  value={formData.availability.hours}
                  onChange={(e) => setFormData({
                    ...formData,
                    availability: {
                      ...formData.availability,
                      hours: e.target.value
                    }
                  })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  placeholder="e.g., 9:00 AM - 6:00 PM"
                />
              </div>
            </div>
          </div>

          {/* Contract & Payment */}
          <div className="bg-gray-900/50 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-white">Contract & Payment</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Payment Terms
                </label>
                <textarea
                  value={formData.payment_terms}
                  onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  rows={3}
                  placeholder="e.g., 50% deposit, 50% on completion"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Contract Details
                </label>
                <textarea
                  value={formData.contract_details}
                  onChange={(e) => setFormData({ ...formData, contract_details: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  rows={3}
                  placeholder="Additional contract terms and conditions"
                />
              </div>
            </div>
          </div>

          {/* Photos */}
          <div className="bg-gray-900/50 p-4 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-white">Photos</h4>
              <button
                type="button"
                onClick={addPhotoUrl}
                className="flex items-center space-x-2 px-3 py-1 bg-primary text-white rounded-md hover:bg-primary-600 text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Add Photo URL</span>
              </button>
            </div>
            {formData.photos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.photos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={photo}
                      alt={`Vendor photo ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No photos added yet</p>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 text-gray-300 hover:text-white disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || formData.service_categories.length === 0}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
            >
              {loading ? 'Saving...' : vendor ? 'Update Vendor' : 'Create Vendor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}